/**
 * Phase 4 slice 4 — Raise a Snag. Responsive (desktop /quality/snags/new +
 * mobile /m/quality/snag). Creating a snag auto-creates a linked Kanban task.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ClipboardList } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import PhotoCaptureField from '../../components/safety/PhotoCaptureField';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';

const SEVERITIES = [
  { v: 'low', label: 'Low' }, { v: 'medium', label: 'Medium' },
  { v: 'high', label: 'High' }, { v: 'critical', label: 'Critical' },
];

const Label = ({ children }) => (
  <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">{children}</label>
);

const TRADES = ['Civil', 'MEP', 'Joinery / Carpentry', 'Painting', 'Flooring', 'Facade', 'Plumbing', 'Electrical', 'HVAC', 'False Ceiling', 'Glass & Aluminium', 'General'];
const ROUND_LABELS = { 1: '1st Snagging', 2: '2nd Snagging', 3: 'De-snag Round', 4: '4th Round' };

export default function RaiseSnag() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    project_id: '', location_on_site: '', area: '', trade: '',
    description: '', severity: 'medium', assigned_to: '', due_date: '', snag_round: 1,
  });
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/projects').then((p) => { setProjects(p); if (p.length) setForm((f) => ({ ...f, project_id: p[0].id })); }).catch(() => toast.error('Failed to load projects'));
    apiGet('/users').then((u) => setUsers(Array.isArray(u) ? u : (u?.users || []))).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.project_id) { toast.error('Select a project'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setSaving(true);
    try {
      const doc = await apiPost('/quality/snags', { ...form, photos_base64: photos });
      toast.success('Snag raised — linked task created');
      navigate(`/app/quality/snags?focus=${doc.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit');
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-0 py-4 sm:py-0">
      <PageHeader overline="Quality" title="Raise Snag" subtitle="Log a finishing-stage defect — a tracking task is created automatically" />
      <div className="space-y-4 bg-white sm:border sm:border-slate-200 rounded-sm sm:p-6" data-testid="raise-snag-form">
        <div>
          <Label>Project</Label>
          <select value={form.project_id} onChange={set('project_id')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="snag-project-select">
            {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
          </select>
        </div>
        <div>
          <Label>Location on site</Label>
          <input value={form.location_on_site} onChange={set('location_on_site')} placeholder="e.g. Block A · Floor 3 · Unit 305" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="snag-location" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Area / Room</Label>
            <input value={form.area} onChange={set('area')} placeholder="e.g. Master Bedroom, Lobby, Toilet" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="snag-area" />
          </div>
          <div>
            <Label>Trade</Label>
            <select value={form.trade} onChange={set('trade')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="snag-trade">
              <option value="">— Select trade —</option>
              {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label>Snagging Round</Label>
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((r) => (
              <button key={r} type="button" onClick={() => setForm({ ...form, snag_round: r })}
                className={`px-3 py-2 rounded-sm text-xs font-bold border transition-colors ${form.snag_round === r ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300'}`}
                data-testid={`snag-round-${r}`}>{ROUND_LABELS[r] || `Round ${r}`}</button>
            ))}
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <textarea value={form.description} onChange={set('description')} rows={3} placeholder="What's the defect?" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="snag-description" />
        </div>
        <div>
          <Label>Severity</Label>
          <div className="grid grid-cols-4 gap-2">
            {SEVERITIES.map((s) => (
              <button key={s.v} type="button" onClick={() => setForm({ ...form, severity: s.v })}
                className={`px-2 py-2.5 rounded-sm text-xs font-bold border transition-colors ${form.severity === s.v ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300'}`}
                data-testid={`snag-severity-${s.v}`}>{s.label}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Assign to</Label>
            <select value={form.assigned_to} onChange={set('assigned_to')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="snag-assignee">
              <option value="">— Unassigned —</option>
              {users.map((u) => <option key={u.email || u.user_id} value={u.email}>{u.name || u.email}</option>)}
            </select>
          </div>
          <div>
            <Label>Due date</Label>
            <input type="date" value={form.due_date} onChange={set('due_date')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="snag-due" />
          </div>
        </div>
        <div>
          <Label>Photos</Label>
          <PhotoCaptureField photos={photos} setPhotos={setPhotos} />
        </div>
        <button onClick={submit} disabled={saving} className="w-full flex items-center justify-center gap-2 bg-[#3b4872] hover:bg-[#2d3856] text-white px-6 py-3.5 rounded-sm disabled:opacity-60 transition-colors font-display font-medium" data-testid="snag-submit-btn">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <ClipboardList size={16} />}
          {saving ? 'Submitting…' : 'Raise Snag'}
        </button>
      </div>
    </div>
  );
}
